<template>
  <div class="qa" v-loading.fullscreen.lock="loading">
    <el-row>
      <ai-breadcrumb />
    </el-row>
    <div class="content-container">
      <div class="content-chat" v-loading="chatLoading">
        <el-scrollbar class="chat-messages" ref="chatMessages" wrap-class="scrollbar-wrapper">
          <div v-for="(message, index) in conversation" :key="index"
            :class="['message', message.role === 'user' ? 'user-message' : 'ai-message']">
            <div class="message-content">{{ message.content }}</div>
          </div>
        </el-scrollbar>
        <div class="input-container">
          <el-row>
            <el-input v-model="userInput" type="textarea" :autosize="{ minRows: 2, maxRows: 10 }"
              :placeholder="$t('gpt.inputQuestion')" class="message-input" @keydown.enter.native="handleInputKeydown" />
          </el-row>
          <el-row>
            <el-button class="send-button" type="primary" icon="el-icon-s-promotion" @click="handleSend"
              :loading="sending" circle title="Send" />
          </el-row>
        </div>
      </div>
      <div class="history-panel">
        <el-card class="history-card" shadow="never">
          <div slot="header" class="clearfix">
            <span class="title">{{ $t('gpt.history') }}</span>
            <el-button class="top-button" type="primary" size="small" icon="el-icon-plus" @click="newConversation"
              circle title="New Conversation"></el-button>
            <el-button class="top-button refresh-button" type="" size="small" icon="el-icon-refresh"
              @click="fetchHistoryList" circle title="Refresh"></el-button>
          </div>
          <el-scrollbar class="history-list" ref="historyListScrollbar"
            wrap-class="scrollbar-wrapper history-list-wrapper" view-class="history-list-content">
            <div class="history-items-container" v-infinite-scroll="loadMoreHistory"
              :infinite-scroll-disabled="historyLoading" :infinite-scroll-distance="10">
              <div v-for="(item, index) in historyList" :key="`${item.id}-${index}`" class="history-item"
                :class="{ 'active': item.id === currentSession }" @click="loadHistoryDetail(item)">
                <span class="summary">{{ item.summary }}</span>
                <div class="meta-container">
                  <span class="update-time">{{ formatDate(item.updatedAt) }}</span>
                  <el-dropdown class="history-item-actions" trigger="click"
                    @command="(command) => handleHistoryAction(command, item)">
                    <el-button class="more-button" type="text" circle @click.stop>
                      <i class="el-icon-more rotate-90"></i>
                    </el-button>
                    <el-dropdown-menu slot="dropdown">
                      <el-dropdown-item command="edit" :icon="'el-icon-edit'">
                        {{ $t('common.edit') }}
                      </el-dropdown-item>
                      <el-dropdown-item command="delete" :icon="'el-icon-delete'">
                        {{ $t('common.delete') }}
                      </el-dropdown-item>
                    </el-dropdown-menu>
                  </el-dropdown>
                </div>
              </div>
              <p v-if="historyLoading" class="history-loading">{{ $t('common.loading') }}...</p>
            </div>
          </el-scrollbar>
        </el-card>
      </div>
    </div>
  </div>
</template>

<script>
import { qaChat, fetchQaChatSessionList, fetchQaChatSessionDetail, updateQaChatSession, deleteQaChatSession } from "@/api/gpt";
import AiBreadcrumb from "@/components/AiBreadcrumb";
import axios from 'axios';

export default {
  components: {
    AiBreadcrumb,
  },
  data() {
    return {
      conversation: [],
      currentSession: "",
      userInput: '',
      sending: false,
      cancelTokenSource: null, // 用于取消请求的 token source
      // History 相关数据
      historyList: [],
      historyTotal: 0,
      historyQuery: {
        offset: 0,
        limit: 10,
      },
      historyLoading: false,
      noMoreHistory: false,
      // 编辑对话框相关数据
      editDialogVisible: false,
      editingSession: null,
      editingSummary: '',
      chatLoading: false,
      loading: false
    };
  },
  mounted() {
    this.fetchHistoryList();
  },
  updated() {
    // 确保滚动条在内容更新后正确显示
    this.$nextTick(() => {
      if (this.$refs.historyListScrollbar) {
        this.$refs.historyListScrollbar.update();
      }
    });
  },
  methods: {
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);

      // 使用本地化格式显示日期
      if (this.$i18n.locale === 'zh' || this.$i18n.locale === 'ko') {
        // 中文格式: 8月15日
        const month = date.getMonth() + 1;
        const day = date.getDate();
        if (this.$i18n.locale === 'ko') {
          return `${month}월${day}일`;
        }
        return `${month}月${day}日`;
      } else {
        // 英文格式: Aug 15
        return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      }
    },

    handleSend() {
      if (!this.userInput.trim() || this.sending) return;

      const question = this.userInput.trim();
      // 添加用户消息到对话记录
      this.conversation.push({
        role: 'user',
        content: question
      });

      this.userInput = '';
      this.sending = true;

      // 滚动到底部
      this.$nextTick(() => {
        this.scrollToBottom();
      });

      // 发送请求到API
      this.sendQAChat(question);
    },

    handleInputKeydown(event) {
      // Shift+Enter：允许默认换行行为
      if (event.shiftKey) {
        return;
      }

      // 普通 Enter：阻止默认换行并触发发送
      event.preventDefault();
      this.handleSend();
    },

    sendQAChat(question) {
      // 创建一个新的 CancelToken
      this.cancelTokenSource = axios.CancelToken.source();

      return qaChat({
        session: this.currentSession,
        question
      }, this.cancelTokenSource.token)
        .then(response => {
          this.sending = false;
          if (response.success) {
            let rst = response.result;
            this.currentSession = rst.session;
            let answer = rst.answer || this.$t("gpt.failAnswer");
            // 添加AI回复到对话记录
            this.conversation.push({
              role: 'assistant',
              content: answer
            });

            // 滚动到底部
            this.$nextTick(() => {
              this.scrollToBottom();
            });

            // 发送消息后刷新历史记录
            this.fetchHistoryList();
          } else {
            // 添加错误消息到对话记录
            this.conversation.push({
              role: 'assistant',
              content: this.$t("gpt.failAnswerDueToNet")
            });

            this.$nextTick(() => {
              this.scrollToBottom();
            });
          }
        }).catch((error) => {
          this.sending = false;
          // 检查是否是由于取消请求引起的错误
          if (axios.isCancel(error)) {
            console.log('Request canceled:', error.message);
            return;
          }

          // 添加错误消息到对话记录
          this.conversation.push({
            role: 'assistant',
            content: this.$t("gpt.failAnswerDueToNet")
          });

          this.$nextTick(() => {
            this.scrollToBottom();
          });
        });
    },

    scrollToBottom() {
      const scrollWrap = this.$refs.chatMessages.wrap;
      scrollWrap.scrollTop = scrollWrap.scrollHeight;
    },

    // 新建对话
    newConversation() {
      this.conversation = [];
      this.currentSession = "";
      this.userInput = "";
    },

    // History 相关方法
    fetchHistoryList() {
      // 重置状态
      this.historyQuery.offset = 0;
      this.noMoreHistory = false;
      this.historyLoading = true;

      fetchQaChatSessionList(this.historyQuery).then((response) => {
        this.historyLoading = false;
        if (response.success) {
          this.historyList = response.result.items || [];
          this.historyTotal = response.result.totalCount;

          // 检查是否已加载所有数据
          if (this.historyList.length >= this.historyTotal) {
            this.noMoreHistory = true;
          }

          // 关键修复：检查内容是否填满容器，未填满则加载更多
          this.$nextTick(() => {
            if (!this.noMoreHistory && this.checkContentFilled()) {
              this.loadMoreHistory();
            }
          });
        }
      }).catch(() => {
        this.historyLoading = false;
      });
    },

    loadMoreHistory() {
      // 如果正在加载或没有更多数据，则不执行
      if (this.historyLoading || this.noMoreHistory) return;

      // 添加额外检查：确保有足够空间才加载
      if (this.checkContentFilled()) {
        this.historyLoading = true;
        this.historyQuery.offset += this.historyQuery.limit;

        fetchQaChatSessionList(this.historyQuery).then((response) => {
          this.historyLoading = false;
          if (response.success) {
            const newItems = response.result.items || [];
            this.historyList = this.historyList.concat(newItems);
            this.historyTotal = response.result.totalCount;

            // 检查是否还有更多数据
            if (this.historyList.length >= this.historyTotal) {
              this.noMoreHistory = true;
            }

            // 检查是否需要继续加载更多数据
            this.$nextTick(() => {
              if (!this.noMoreHistory && this.checkContentFilled()) {
                this.loadMoreHistory();
              }
            });
          } else {
            this.noMoreHistory = true;
          }
        }).catch(() => {
          this.historyLoading = false;
          this.historyQuery.offset -= this.historyQuery.limit; // 恢复偏移量
        });
      }
    },

    loadHistoryDetail(row) {
      // 如果有正在进行的请求，取消它
      if (this.cancelTokenSource) {
        this.cancelTokenSource.cancel('Operation canceled by the user.');
        this.cancelTokenSource = null;
      }
      this.chatLoading = true;

      // 取消发送状态
      this.sending = false;

      fetchQaChatSessionDetail(row.id).then((response) => {
        if (response.success) {
          // 将历史记录转换为对话格式并加载到聊天区域
          this.conversation = response.result.items.map(item => {
            return {
              role: item.role === 'human' ? 'user' : 'assistant',
              content: item.content
            };
          });

          // 设置当前会话ID，以便继续对话
          this.currentSession = row.id;

          this.$nextTick(() => {
            this.scrollToBottom();
          });
        }
      }).finally(() => {
        this.chatLoading = false;
      });
    },

    // 检查历史记录列表是否填满容器
    checkContentFilled() {
      const scrollbar = this.$refs.historyListScrollbar;
      if (!scrollbar || !scrollbar.wrap) return false;

      // 检查内容高度是否小于容器高度
      return scrollbar.wrap.scrollHeight <= scrollbar.wrap.clientHeight;
    },

    handleHistoryPageChange(page) {
      this.historyQuery.page = page;
      this.fetchHistoryList();
    },

    handleHistoryAction(command, item) {
      switch (command) {
        case 'edit':
          this.$prompt(this.$t('gpt.enterNewSummary'), this.$t('gpt.editSession'), {
            confirmButtonText: this.$t('common.submit'),
            cancelButtonText: this.$t('common.cancel'),
            inputValue: item.summary,
            inputPattern: /\S/,
            inputErrorMessage: this.$t('gpt.summaryRequired')
          }).then(({ value }) => {
            this.updateQaSession(item, value);
          }).catch(() => {
            // 用户取消操作
          });
          break;
        case 'delete':
          this.$confirm(this.$t('gpt.deleteSessionPrompt'), this.$t('gpt.deleteSession'), {
            confirmButtonText: this.$t('common.submit'),
            cancelButtonText: this.$t('common.cancel'),
            type: 'warning'
          }).then(() => {
            this.deleteSession(item);
          }).catch(() => {
            // 用户取消操作
          });
          break;
      }
    },

    updateQaSession(session, summary) {
      this.loading = true;
      updateQaChatSession(session.id, { summary: summary.trim() })
        .then(response => {
          if (response.success) {
            this.$message.success(this.$t('common.saveSucc'));
            // 更新本地列表中的摘要
            const index = this.historyList.findIndex(item => item.id === session.id);
            if (index !== -1) {
              this.historyList[index].summary = summary.trim();
            }
            // 如果当前会话是正在编辑的会话，也需要更新
            if (this.currentSession === session.id) {
              this.currentSession = session.id;
            }
          }
        }).finally(() => {
          this.loading = false;
        });
    },

    deleteSession(item) {
      this.loading = true;
      deleteQaChatSession(item.id)
        .then(response => {
          if (response.success) {
            this.$message.success(this.$t('common.deleteSucc'));
            // 从本地列表中移除
            const index = this.historyList.findIndex(historyItem => historyItem.id === item.id);
            if (index !== -1) {
              this.historyList.splice(index, 1);
            }
            // 如果当前会话是被删除的会话，清空聊天区域
            if (this.currentSession === item.id) {
              this.newConversation();
            }
          }
        }).finally(() => {
          this.loading = false;
        });
    }
  }
}
</script>

<style lang="scss" scoped>
.qa {
  ::v-deep .el-scrollbar {
    .el-scrollbar__wrap {
      overflow-x: hidden !important;
    }
  }

  .content-container {
    display: flex;
  }

  .content-chat {
    flex: 1;
    margin-right: 20px;
    display: flex;
    flex-direction: column;
    height: calc(100vh - 152px);
  }

  .history-panel {
    width: 300px;
    font-size: 14px;
    height: calc(100vh - 152px);
    /* 确保父容器有固定高度 */

    .history-card {
      border: none;
      height: 100%;
      /* 占满父容器高度 */
      display: flex;
      flex-direction: column;

      .title {
        line-height: 32px;
        font-size: 15px;
        font-weight: bold;
        margin-left: 5px;
      }

      .top-button {
        float: right;
        margin-left: 10px;
        padding: 6px;
        font-size: 18px;
      }

      ::v-deep .el-card__header {
        padding: 10px;
        flex-shrink: 0;
        /* 防止头部被压缩 */
      }

      ::v-deep .el-card__body {
        padding: 5px;
        flex: 1;
        display: flex;
        flex-direction: column;
        overflow: hidden;
        /* 防止内容溢出 */
        min-height: 0;
        /* 修复flex容器中子元素高度计算问题 */
      }

      .history-list {
        flex: 1;
        min-height: 0;
        /* 修复flex容器中子元素高度计算问题 */

        ::v-deep .scrollbar-wrapper {
          padding: 0 5px 0 5px;
        }

        .history-items-container {
          min-height: 100%;
          margin-top: 3px;
          margin-bottom: 3px;
        }

        .history-item {
          padding: 8px 10px;
          background-color: #f5f7fa;
          border-radius: 12px;
          margin-bottom: 8px;
          cursor: pointer;
          transition: background-color 0.3s;
          display: flex;
          align-items: center;
          position: relative;

          &.active,
          &:hover {
            background-color: rgb(234, 236, 239);
          }

          &:last-child {
            margin-bottom: 0;
          }

          >.summary {
            display: inline-block;
            width: 68%;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }

          .meta-container {
            width: 32%;
            display: flex;
            justify-content: flex-end;
            align-items: center;
            position: relative;

            .update-time {
              font-size: 12px;
              text-align: right;
              color: #909399;
              transition: padding-right 0.3s;
            }

            .history-item-actions {
              position: absolute;
              display: none;

              .more-button {
                color: #606266;
                font-size: 14px;
                padding: 5px;
                border: none;
                background: transparent;

                .rotate-90 {
                  transform: rotate(90deg);
                  display: inline-block;
                }

                &:hover {
                  background-color: rgba(0, 0, 0, 0.05);
                }
              }
            }
          }

          &:hover {
            .meta-container {
              .update-time {
                padding-right: 30px;
              }

              .history-item-actions {
                display: block;
              }
            }
          }
        }

        .history-loading {
          text-align: center;
          color: #909399;
          font-size: 12px;
          padding: 10px 0;
        }
      }
    }
  }

  .chat-messages {
    flex: 1;
    border-radius: 8px;
    margin-bottom: 10px;
  }

  .message {
    display: flex;
    margin-right: 10px;
    margin-bottom: 15px;
  }

  .ai-message {
    justify-content: flex-start;
  }

  .user-message {
    justify-content: flex-end;
  }

  .message-content {
    max-width: 80%;
    padding: 8px 12px;
    border-radius: 12px;
    word-wrap: break-word;
    white-space: pre-wrap;
  }

  .ai-message .message-content {
    background-color: #ffffff;
    border-top-left-radius: 4px;
  }

  .user-message .message-content {
    background-color: #1e88e5;
    color: white;
    border-bottom-right-radius: 4px;
  }

  .input-container {
    position: relative;
    border: 1px solid #dcdfe6;
    border-radius: 12px;
    padding: 10px 10px;
    transition: border-color 0.3s;
    background-color: #ffffff;

    &:focus-within {
      border-color: #1e88e5;
    }

    .message-input {
      width: 100%;
      border: none !important;
      outline: none !important;
      resize: none;
      overflow: hidden;
      font-size: 16px;

      ::v-deep .el-textarea__inner {
        border: none !important;
        outline: none !important;
        box-shadow: none !important;
        resize: none;
        overflow: hidden;
        padding: 0 !important;
      }
    }

    .send-button {
      margin-top: 8px;
      float: right;
      padding: 8px;
      font-size: 18px;
    }
  }
}
</style>